import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Users, 
  Building2, 
  Calendar, 
  Bell, 
  CreditCard, 
  BarChart3,
  Shield,
  Save,
  RotateCcw,
  Check,
  X
} from 'lucide-react';
import { authAPI } from '../../services/api';
import { useI18n } from '../../contexts/I18nContext';
import {
  readStoredMenuPermissions,
  storedToLegacyArray,
  writeStoredMenuPermissions,
  legacyArrayToStored,
  mergeStoredMenuPermissionsWithDefaults,
} from '../../utils/menuPermissionsStorage';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  description: string;
  category: 'main' | 'admin' | 'reports';
}

interface RolePermissions {
  role: string;
  permissions: {
    [menuId: string]: boolean;
  };
}

interface ResourcePermission {
  resource: string;
  label: string;
  actions: {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
  };
}

interface ResourcePermissions {
  role: string;
  resources: {
    [resourceId: string]: {
      create: boolean;
      read: boolean;
      update: boolean;
      delete: boolean;
    };
  };
}

const MenuPermissions: React.FC = () => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<'menu' | 'crud'>('menu');
  
  const roles = [
    { id: 'admin', label: t('administrator'), color: '#dc2626' },
    { id: 'supervisor', label: t('supervisor'), color: '#ea580c' },
    { id: 'employee', label: t('employee'), color: '#0891b2' },
    { id: 'client', label: t('clientRole'), color: '#059669' }
  ];

  const resources: ResourcePermission[] = [
    { resource: 'appointments', label: 'Agendamentos', actions: { create: false, read: false, update: false, delete: false } },
    { resource: 'clients', label: 'Clientes', actions: { create: false, read: false, update: false, delete: false } },
    { resource: 'properties', label: 'Propriedades', actions: { create: false, read: false, update: false, delete: false } },
    { resource: 'users', label: 'Usuários', actions: { create: false, read: false, update: false, delete: false } },
    { resource: 'services', label: 'Serviços', actions: { create: false, read: false, update: false, delete: false } },
    { resource: 'payments', label: 'Pagamentos', actions: { create: false, read: false, update: false, delete: false } },
  ];

  const defaultResourcePermissions: { [role: string]: { [resource: string]: { create: boolean; read: boolean; update: boolean; delete: boolean } } } = {
    admin: {
      appointments: { create: true, read: true, update: true, delete: true },
      clients: { create: true, read: true, update: true, delete: true },
      properties: { create: true, read: true, update: true, delete: true },
      users: { create: true, read: true, update: true, delete: true },
      services: { create: true, read: true, update: true, delete: true },
      payments: { create: true, read: true, update: true, delete: true },
    },
    supervisor: {
      appointments: { create: true, read: true, update: true, delete: true },
      clients: { create: false, read: true, update: false, delete: false },
      properties: { create: false, read: true, update: false, delete: false },
      users: { create: false, read: false, update: false, delete: false },
      services: { create: false, read: true, update: false, delete: false },
      payments: { create: false, read: true, update: false, delete: false },
    },
    employee: {
      appointments: { create: false, read: true, update: true, delete: false },
      clients: { create: false, read: true, update: false, delete: false },
      properties: { create: false, read: true, update: false, delete: false },
      users: { create: false, read: false, update: false, delete: false },
      services: { create: false, read: true, update: false, delete: false },
      payments: { create: false, read: false, update: false, delete: false },
    },
    client: {
      appointments: { create: true, read: true, update: false, delete: true },
      clients: { create: false, read: false, update: false, delete: false },
      properties: { create: true, read: true, update: true, delete: false },
      users: { create: false, read: false, update: false, delete: false },
      services: { create: false, read: true, update: false, delete: false },
      payments: { create: false, read: true, update: false, delete: false },
    },
  };

  const [resourcePermissions, setResourcePermissions] = useState<ResourcePermissions[]>([]);

  const [menuItems] = useState<MenuItem[]>([
    // Menu Principal
    { id: 'dashboard', label: 'Dashboard', icon: 'Home', path: '/', description: 'Visão geral do sistema', category: 'main' },
    { id: 'clientes', label: 'Clientes', icon: 'Users', path: '/clientes', description: 'Gerenciar clientes', category: 'main' },
    { id: 'propriedades', label: 'Propriedades', icon: 'Building2', path: '/propriedades', description: 'Gerenciar propriedades', category: 'main' },
    { id: 'agendamentos', label: 'Agendamentos', icon: 'Calendar', path: '/agendamentos', description: 'Gerenciar agendamentos', category: 'main' },
    { id: 'financeiro', label: 'Financeiro', icon: 'CreditCard', path: '/financeiro', description: 'Gestão financeira', category: 'main' },
    
    // Menu Administrativo
    { id: 'usuarios', label: 'Usuários', icon: 'Users', path: '/admin/usuarios', description: 'Gerenciar usuários do sistema', category: 'admin' },
    { id: 'permissoes-menu', label: 'Permissões de Menu', icon: 'Settings', path: '/admin/permissoes-menu', description: 'Configurar permissões de menu', category: 'admin' },
    { id: 'configuracoes', label: 'Configurações', icon: 'Settings', path: '/configuracoes', description: 'Configurações do sistema', category: 'admin' },
    
    // Menu Relatórios
    { id: 'relatorios', label: 'Relatórios', icon: 'BarChart3', path: '/relatorios', description: 'Relatórios e análises', category: 'reports' },
    { id: 'notificacoes', label: 'Notificações', icon: 'Bell', path: '/notificacoes', description: 'Central de notificações', category: 'main' },
    { id: 'ajuda', label: 'Ajuda', icon: 'CircleHelp', path: '/ajuda', description: 'Vídeos tutoriais e central de ajuda', category: 'main' }
  ]);

  const [permissions, setPermissions] = useState<RolePermissions[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Permissões padrão para cada role
  const defaultPermissions: { [role: string]: string[] } = {
    admin: ['dashboard', 'clientes', 'propriedades', 'agendamentos', 'financeiro', 'usuarios', 'permissoes-menu', 'configuracoes', 'relatorios', 'notificacoes', 'ajuda'],
    supervisor: ['dashboard', 'clientes', 'propriedades', 'agendamentos', 'financeiro', 'relatorios', 'notificacoes', 'ajuda'],
    employee: ['dashboard', 'clientes', 'propriedades', 'agendamentos', 'notificacoes', 'ajuda'],
    client: ['dashboard', 'agendamentos', 'notificacoes', 'ajuda']
  };

  useEffect(() => {
    loadPermissions();
    loadResourcePermissions();
  }, []);

  const loadResourcePermissions = async () => {
    try {
      const saved = localStorage.getItem('resource-permissions');
      if (saved) {
        const parsed = JSON.parse(saved);
        setResourcePermissions(Array.isArray(parsed) ? parsed : []);
      } else {
        // Carregar permissões padrão
        const defaultPerms = roles.map(role => ({
          role: role.id,
          resources: defaultResourcePermissions[role.id] || {}
        }));
        setResourcePermissions(defaultPerms);
      }
    } catch (error) {
      console.error('Erro ao carregar permissões de recursos:', error);
      const defaultPerms = roles.map(role => ({
        role: role.id,
        resources: defaultResourcePermissions[role.id] || {}
      }));
      setResourcePermissions(defaultPerms);
    }
  };

  const toggleResourcePermission = (roleId: string, resourceId: string, action: 'create' | 'read' | 'update' | 'delete') => {
    setResourcePermissions(prev => prev.map(rolePerms => {
      if (rolePerms.role === roleId) {
        return {
          ...rolePerms,
          resources: {
            ...rolePerms.resources,
            [resourceId]: {
              ...rolePerms.resources[resourceId],
              [action]: !rolePerms.resources[resourceId]?.[action]
            }
          }
        };
      }
      return rolePerms;
    }));
    setHasChanges(true);
  };

  const loadPermissions = async () => {
    setIsLoading(true);
    try {
      const stored = readStoredMenuPermissions();
      const menuIds = menuItems.map(item => item.id);
      setPermissions(storedToLegacyArray(stored, menuIds));
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
    }
    setIsLoading(false);
  };

  const savePermissions = async () => {
    setIsLoading(true);
    try {
      const stored = mergeStoredMenuPermissionsWithDefaults(legacyArrayToStored(permissions));
      writeStoredMenuPermissions(stored);
      localStorage.setItem('resource-permissions', JSON.stringify(resourcePermissions));
      
      // Simular delay da API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setHasChanges(false);
      alert(t('menuPermissions.saved'));
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
      alert(t('menuPermissions.saveError'));
    }
    setIsLoading(false);
  };

  const resetToDefaults = () => {
    if (confirm('Tem certeza que deseja restaurar as permissões padrão? Esta ação não pode ser desfeita.')) {
      // Resetar permissões de menu
      const defaultPerms = roles.map(role => ({
        role: role.id,
        permissions: menuItems.reduce((acc, item) => {
          acc[item.id] = defaultPermissions[role.id]?.includes(item.id) || false;
          return acc;
        }, {} as { [key: string]: boolean })
      }));
      setPermissions(defaultPerms);
      
      // Resetar permissões CRUD
      const defaultResourcePerms = roles.map(role => ({
        role: role.id,
        resources: defaultResourcePermissions[role.id] || {}
      }));
      setResourcePermissions(defaultResourcePerms);
      
      setHasChanges(true);
    }
  };

  const togglePermission = (roleId: string, menuId: string) => {
    setPermissions(prev => prev.map(rolePerms => {
      if (rolePerms.role === roleId) {
        return {
          ...rolePerms,
          permissions: {
            ...rolePerms.permissions,
            [menuId]: !rolePerms.permissions[menuId]
          }
        };
      }
      return rolePerms;
    }));
    setHasChanges(true);
  };

  const toggleAllForRole = (roleId: string) => {
    setPermissions(prev => prev.map(rolePerms => {
      if (rolePerms.role === roleId) {
        const allEnabled = Object.values(rolePerms.permissions).every(Boolean);
        const newPermissions = { ...rolePerms.permissions };
        Object.keys(newPermissions).forEach(key => {
          newPermissions[key] = !allEnabled;
        });
        return {
          ...rolePerms,
          permissions: newPermissions
        };
      }
      return rolePerms;
    }));
    setHasChanges(true);
  };

  const getIcon = (iconName: string) => {
    const icons: { [key: string]: React.ComponentType<any> } = {
      Home: Settings,
      Users,
      Building2,
      Calendar,
      CreditCard,
      BarChart3,
      Settings,
      Bell
    };
    return icons[iconName] || Settings;
  };

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      main: t('mainMenu'),
      admin: t('administration'),
      reports: t('reports')
    };
    return labels[category] || category;
  };

  const groupedMenuItems = menuItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as { [key: string]: MenuItem[] });

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#666'
      }}>{t('menuPermissions.loading')}</div>
    );
  }

  return (
    <div style={{ padding: '30px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', color: '#333' }}>{t('menuPermissions')}</h1>
        <p style={{ color: '#666', margin: 0 }}>
          {t('menuPermissionsSubtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <button
          onClick={() => setActiveTab('menu')}
          style={{
            padding: '12px 24px',
            border: 'none',
            backgroundColor: 'transparent',
            borderBottom: activeTab === 'menu' ? '2px solid #059669' : '2px solid transparent',
            color: activeTab === 'menu' ? '#059669' : '#6b7280',
            cursor: 'pointer',
            fontWeight: activeTab === 'menu' ? '600' : '400',
            fontSize: '14px'
          }}
        >{t('menuPermissions.menuTitle')}</button>
        <button
          onClick={() => setActiveTab('crud')}
          style={{
            padding: '12px 24px',
            border: 'none',
            backgroundColor: 'transparent',
            borderBottom: activeTab === 'crud' ? '2px solid #059669' : '2px solid transparent',
            color: activeTab === 'crud' ? '#059669' : '#6b7280',
            cursor: 'pointer',
            fontWeight: activeTab === 'crud' ? '600' : '400',
            fontSize: '14px'
          }}
        >{t('menuPermissions.crudTitle')}</button>
      </div>

      {/* Botões de Ação */}
      <div style={{ 
        display: 'flex', 
        gap: '15px', 
        marginBottom: '30px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <button
          onClick={savePermissions}
          disabled={!hasChanges || isLoading}
          style={{
            backgroundColor: hasChanges ? '#059669' : '#d1d5db',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            cursor: hasChanges ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <Save style={{ fontSize: '16px' }} />
          {isLoading ? t('saving') : t('saveChanges')}
        </button>

        <button
          onClick={resetToDefaults}
          disabled={isLoading}
          style={{
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <RotateCcw style={{ fontSize: '16px' }} />
{t('restoreDefaults')}
        </button>

        {hasChanges && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: '#ea580c',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              backgroundColor: '#ea580c',
              borderRadius: '50%'
            }} />
{t('unsavedChanges')}
          </div>
        )}
      </div>

      {/* Tabela de Permissões */}
      {activeTab === 'menu' ? (
        /* TABELA DE PERMISSÕES DE MENU */
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '200px repeat(4, 1fr)',
            borderBottom: '2px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            {/* Header */}
            <div style={{ padding: '16px', fontWeight: '600', color: '#374151' }}>
              {t('menuProfile')}
            </div>
            {roles.map(role => (
              <div key={role.id} style={{ 
                padding: '16px', 
                textAlign: 'center',
                borderLeft: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: role.color,
                    borderRadius: '50%'
                  }} />
                  <span style={{ fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                    {role.label}
                  </span>
                  <button
                    onClick={() => toggleAllForRole(role.id)}
                    style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      color: '#6b7280'
                    }}
                  >
                    {permissions.length > 0 && Object.values(permissions.find(p => p.role === role.id)?.permissions || {}).every(Boolean) ? t('deselectAll') : t('selectAll')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Menu Items por Categoria */}
          {Object.entries(groupedMenuItems).map(([category, items]) => (
            <div key={category}>
              {/* Cabeçalho da Categoria */}
              <div style={{
                backgroundColor: '#f3f4f6',
                padding: '12px 16px',
                borderBottom: '1px solid #e5e7eb',
                fontWeight: '600',
                color: '#374151',
                fontSize: '14px'
              }}>
                {getCategoryLabel(category)}
              </div>

              {/* Itens do Menu */}
              {items.map(item => (
                <div key={item.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '200px repeat(4, 1fr)',
                  borderBottom: '1px solid #f3f4f6',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  {/* Item do Menu */}
                  <div style={{ 
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {React.createElement(getIcon(item.icon), { 
                      style: { fontSize: '16px', color: '#6b7280' } 
                    })}
                    <div>
                      <div style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>
                        {item.label}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '12px' }}>
                        {item.description}
                      </div>
                    </div>
                  </div>

                  {/* Permissões por Role */}
                  {roles.map(role => {
                    const rolePermission = permissions.length > 0 ? permissions.find(p => p.role === role.id) : null;
                    const isEnabled = rolePermission?.permissions[item.id] || false;
                    
                    return (
                      <div key={role.id} style={{ 
                        padding: '16px', 
                        textAlign: 'center',
                        borderLeft: '1px solid #f3f4f6'
                      }}>
                        <button
                          onClick={() => togglePermission(role.id, item.id)}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '4px',
                            border: '2px solid',
                            borderColor: isEnabled ? role.color : '#d1d5db',
                            backgroundColor: isEnabled ? role.color : 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                        >
                          {isEnabled && (
                            <Check style={{ fontSize: '12px', color: 'white' }} />
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        /* TABELA DE PERMISSÕES CRUD */
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '200px repeat(4, 1fr)',
            borderBottom: '2px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            {/* Header */}
            <div style={{ padding: '16px', fontWeight: '600', color: '#374151' }}>{t('menuPermissions.resourceAction')}</div>
            {roles.map(role => (
              <div key={role.id} style={{ 
                padding: '16px', 
                textAlign: 'center',
                borderLeft: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    backgroundColor: role.color,
                    borderRadius: '50%'
                  }} />
                  <span style={{ fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                    {role.label}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Recursos com ações CRUD */}
          {resources.map(resource => {
            const resourcePerms = resourcePermissions.find(rp => rp.role === 'admin');
            const resourceData = resourcePerms?.resources[resource.resource] || resource.actions;
            
            return (
              <div key={resource.resource}>
                {/* Nome do Recurso */}
                <div style={{
                  backgroundColor: '#f3f4f6',
                  padding: '12px 16px',
                  borderBottom: '1px solid #e5e7eb',
                  fontWeight: '600',
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  {resource.label}
                </div>

                {/* Ações CRUD */}
                {(['create', 'read', 'update', 'delete'] as const).map(action => (
                  <div key={action} style={{
                    display: 'grid',
                    gridTemplateColumns: '200px repeat(4, 1fr)',
                    borderBottom: '1px solid #f3f4f6',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {/* Nome da Ação */}
                    <div style={{ 
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      paddingLeft: '32px'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500', color: '#374151', fontSize: '14px', textTransform: 'capitalize' }}>
                          {action === 'create' ? 'Criar' : 
                           action === 'read' ? 'Ler' : 
                           action === 'update' ? 'Atualizar' : 
                           'Deletar'}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '12px' }}>
                          {action === 'create' ? 'Criar novos registros' : 
                           action === 'read' ? 'Visualizar registros' : 
                           action === 'update' ? 'Modificar registros' : 
                           'Remover registros'}
                        </div>
                      </div>
                    </div>

                    {/* Permissões por Role */}
                    {roles.map(role => {
                      const roleResourcePerms = resourcePermissions.find(rp => rp.role === role.id);
                      const isEnabled = roleResourcePerms?.resources[resource.resource]?.[action] || false;
                      
                      return (
                        <div key={role.id} style={{ 
                          padding: '16px', 
                          textAlign: 'center',
                          borderLeft: '1px solid #f3f4f6'
                        }}>
                          <button
                            onClick={() => toggleResourcePermission(role.id, resource.resource, action)}
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                              border: '2px solid',
                              borderColor: isEnabled ? role.color : '#d1d5db',
                              backgroundColor: isEnabled ? role.color : 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s'
                            }}
                          >
                            {isEnabled && (
                              <Check style={{ fontSize: '12px', color: 'white' }} />
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Legenda */}
      <div style={{
        marginTop: '20px',
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '6px',
        fontSize: '14px',
        color: '#6b7280'
      }}>
        {activeTab === 'menu' ? (
          <>
            <strong>{t('legend')}:</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li><strong>{t('mainMenu')}:</strong> {t('mainMenuDesc')}</li>
              <li><strong>{t('administration')}:</strong> {t('administrationDesc')}</li>
              <li><strong>{t('reports')}:</strong> {t('reportsDesc')}</li>
            </ul>
            <p style={{ margin: '8px 0 0 0' }}>
              <strong>{t('tip')}:</strong> {t('tipText')}
            </p>
          </>
        ) : (
          <>
            <strong>{t('permissions.legend')}</strong>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li><strong>{t('permissions.createDesc')}</strong></li>
              <li><strong>{t('permissions.readDesc')}</strong></li>
              <li><strong>{t('permissions.updateDesc')}</strong></li>
              <li><strong>{t('permissions.deleteDesc')}</strong></li>
            </ul>
            <p style={{ margin: '8px 0 0 0' }}>
              <strong>{t('permissions.tipTitle')}</strong> {t('permissions.tipText')}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default MenuPermissions;
